import { useState } from "react";

import { QUICK_MENU_URLS } from "@/constants/quickMenu";
import { groupQuickMenuChoices, isSameUrlSet } from "@/lib/quickMenu";
import { useQuickMenuStore } from "@/store/useQuickMenuStore";

import { useNavSections } from "./useNavSections";

/**
 * 자주찾는 메뉴 설정 — 어떤 페이지를 띠에 올릴지 고르는 다이얼로그의 상태.
 *
 * 다이얼로그 안의 체크는 **초안**(`draft`)에만 쓰이고, `저장` 을 눌러야 스토어에 들어간다.
 * 그래서 취소하거나 바깥을 눌러 닫으면 아무것도 바뀌지 않는다. 열 때마다 초안은 저장된
 * 선택(없으면 기본 목록)에서 다시 시작한다. 초안이 기본 목록과 같은 집합으로 저장되면
 * "고른 적 없음"(null)으로 돌려, 나중에 기본이 바뀌면 따라가게 둔다.
 */
export function useQuickMenuSettings() {
  const sections = useNavSections();
  const selectedUrls = useQuickMenuStore((state) => state.selectedUrls);
  const setSelectedUrls = useQuickMenuStore((state) => state.setSelectedUrls);
  const resetSelectedUrls = useQuickMenuStore(
    (state) => state.resetSelectedUrls,
  );
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(QUICK_MENU_URLS);

  const onOpenChange = (next: boolean) => {
    if (next) setDraft(selectedUrls ?? QUICK_MENU_URLS);
    setOpen(next);
  };

  const onToggle = (url: string) =>
    setDraft((current) =>
      current.includes(url)
        ? current.filter((item) => item !== url)
        : [...current, url],
    );

  const onReset = () => setDraft(QUICK_MENU_URLS);

  const onSave = () => {
    if (isSameUrlSet(draft, QUICK_MENU_URLS)) resetSelectedUrls();
    else setSelectedUrls(draft);
    setOpen(false);
  };

  const groups = groupQuickMenuChoices(sections, draft);
  const selectedCount = groups.reduce(
    (count, group) =>
      count + group.choices.filter((choice) => choice.selected).length,
    0,
  );

  return {
    openDialog: () => onOpenChange(true),
    dialog: {
      open,
      onOpenChange,
      groups,
      selectedCount,
      onToggle,
      onSave,
      onReset,
    },
  };
}
